import { TPopupProps } from '@@types/popup';
import { IAddress, TAddressUpdate } from '@@types/address';
import {
  Divider,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  ModalFooter,
  Stack,
  useToast,
} from '@chakra-ui/react';
import Content from '@components/content';
import queryKeys from '@constants/query-keys';
import AddressEditModal from '@containers/address/popups/address-edit.modal';
import {
  getAddress,
  useAddressDelete,
  useAddressUpdate,
  useChangeCurrentAddress,
} from '@containers/address/queries';
import usePopup from '@hooks/usePopup';
import useUpload from '@hooks/useUpload';
import { addressSelector, setCurrentAddress } from '@stores/address';
import { initCart } from '@stores/cart';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaAngleRight } from 'react-icons/fa';
import { GrCamera } from 'react-icons/gr';
import { HiOutlineLocationMarker } from 'react-icons/hi';
import { IoCheckmarkDoneSharp } from 'react-icons/io5';
import { MdOutlineGpsFixed } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { setMyInfoSpread } from '@stores/myInfo';
import ROUTER from '@constants/router';

export default function AddressControlModal({
  closePopup,
  props,
}: TPopupProps<{}>) {
  const router = useRouter();
  const { t } = useTranslation('address');
  const { currentAddress } = useSelector(addressSelector);
  const handleMoveAddressSetting = () => {
    router.push({ pathname: ROUTER.address }, undefined, {
      shallow: true,
    });
    closePopup();
  };

  return (
    <DrawerContent maxH="100vh" overflow="hidden">
      <DrawerCloseButton zIndex={20} />
      <DrawerHeader textAlign="center" borderBottomWidth="1px" py="20px">
        <Content>
          <div className="mb-[20px] relative font-bold px-[4px]">
            <p>{t('set_address')}</p>
          </div>
          {currentAddress && (
            <div className="flex items-center gap-[18px]">
              <div>
                <HiOutlineLocationMarker />
              </div>
              <div className="text-start">
                <p className="text-[15px]">{currentAddress.alias}</p>
                <p className="text-sm font-medium">{currentAddress.address}</p>
                <p className="text-[12px] font-medium">
                  {currentAddress.detail}
                </p>
              </div>
            </div>
          )}
          <Divider my="12px" />
          <button
            className="text-sm w-full flex justify-between items-center"
            onClick={handleMoveAddressSetting}
          >
            <div className="flex items-center gap-[6px]">
              <MdOutlineGpsFixed />
              <span>{t('new_address_setup_prompt')}</span>
            </div>
            <FaAngleRight />
          </button>
        </Content>
      </DrawerHeader>
      <DrawerBody overflow="auto" px="0" pt="0">
        <AddressControlModalBody closePopup={closePopup} />
      </DrawerBody>
      <ModalFooter />
    </DrawerContent>
  );
}

function AddressControlModalBody({ closePopup }: { closePopup: () => void }) {
  const { data: addressData } = getAddress();
  const { currentAddress } = useSelector(addressSelector);
  const { t } = useTranslation('addressModal');
  const toast = useToast();
  const queryClient = useQueryClient();
  const router = useRouter();
  const { mutate: updateMutate } = useAddressUpdate({
    onMutate: (variable: TAddressUpdate) => {
      queryClient.setQueryData<IAddress[]>(
        [...queryKeys.ADDRESS.ALL],
        (prev) => {
          if (!prev) return prev;
          return prev.map((_prev) =>
            _prev._id === variable.id ? { ..._prev, ...variable } : _prev,
          );
        },
      );
    },
    onError: () => {
      toast({
        status: 'error',
        description: '주소 수정 시 에러가 발생했습니다.',
        duration: 3000,
      });
      router.reload();
    },
  });

  const dispatch = useDispatch();
  const { openAlert, openPromisePopup } = usePopup();
  const { mutate } = useChangeCurrentAddress({
    onMutate: ({ id }) => {
      const targetAddress = addressData?.find((address) => address._id === id);

      if (targetAddress)
        dispatch(setMyInfoSpread({ currentAddress: targetAddress }));
      return { prevAddress: currentAddress };
    },
    onError: (_, __, ctx) => {
      dispatch(
        setMyInfoSpread({
          currentAddress: (ctx as { prevAddress: IAddress }).prevAddress,
        }),
      );
    },
  });

  const { mutateAsync } = useAddressDelete({
    onMutate: (id: string) => {
      const currentAddress = queryClient.getQueryData<IAddress[]>([
        ...queryKeys.ADDRESS.ALL,
      ]);
      if (!currentAddress) return;
      const filtered = currentAddress.filter((item) => item._id !== id);
      queryClient.setQueryData<IAddress[]>([...queryKeys.ADDRESS.ALL], () => {
        return filtered;
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries([...queryKeys.ADDRESS.ALL]);
    },
  });

  const handleCurrentAddressChange = (address: IAddress) => {
    if (address._id === currentAddress?._id) {
      return;
    }
    dispatch(setCurrentAddress(address));
    dispatch(initCart({}));
    mutate({ id: address._id });
    toast({
      description: (
        <span className="text-sm">
          {t('address:current_address_changed', { alias: address.alias })}
        </span>
      ),
      duration: 3000,
      position: 'bottom',
      isClosable: true,
      variant: 'top-accent',
    });
    closePopup();
  };

  const handleAddressEdit = async (
    e: React.MouseEvent<HTMLElement, MouseEvent>,
    editProps: {
      id: string;
      alias: string;
      isCurrentAddress: boolean;
      detail: string;
    },
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const result = await openPromisePopup<'promiseModal', TAddressUpdate>({
      isOpen: true,
      Component: AddressEditModal,
      props: {
        ...editProps,
      },
      type: 'promiseModal',
    });

    if (!result) return;
    updateMutate({
      ...result,
    });
  };

  const handleAddressDelete = async (
    e: React.MouseEvent<HTMLElement, MouseEvent>,
    {
      id,
      alias,
    }: {
      id: string;
      alias: string;
    },
  ) => {
    e.preventDefault();
    e.stopPropagation();
    await openAlert({
      description: (
        <div className="py-[24px]">
          {t('address:confirm_address_delete', { alias })}
        </div>
      ),
      cancelButtonText: '' + t('common:cancel'),
      confirmButtonText: '' + t('common:delete'),
      confirmButtonType: 'danger',
      handleClosePopup: async (close, setIsLoading) => {
        setIsLoading(true);
        await mutateAsync(id);
        setIsLoading(false);
        close(true);
      },
    });
  };

  return (
    <div className="">
      <Stack divider={<Divider />}>
        <Content className="mb-[40px] w-full">
          <Stack divider={<Divider />}>
            {addressData &&
              addressData.map((address) => (
                <div
                  key={address._id}
                  className={
                    address.isAbled ? 'cursor-pointer' : 'cursor-not-allowed'
                  }
                  onClick={() => handleCurrentAddressChange(address)}
                >
                  <div className="flex items-center justify-between py-[10px] gap-[20px]">
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-[18px]">
                        <div className="text-[20px] font-bold">
                          <HiOutlineLocationMarker />
                        </div>
                        <div className="text-start">
                          <div className="flex items-center gap-[6px]">
                            <p className="text-[15px] font-bold">
                              {address.alias}
                            </p>
                            <button
                              className="bg-secondary-200 text-[12px] px-[6px] rounded-lg"
                              onClick={(e) =>
                                handleAddressEdit(e, {
                                  id: address._id,
                                  alias: address.alias,
                                  isCurrentAddress: false,
                                  detail: address.detail,
                                })
                              }
                              type="button"
                            >
                              {t('common:edit')}
                            </button>
                            <button
                              className="bg-secondary-200 text-[12px] px-[6px] rounded-lg"
                              onClick={(e) =>
                                handleAddressDelete(e, {
                                  id: address._id,
                                  alias: address.alias,
                                })
                              }
                              type="button"
                            >
                              {t('common:delete')}
                            </button>
                          </div>
                          <p className="text-sm font-medium">
                            {address.address}
                          </p>
                          <p className="text-[12px] font-medium">
                            {address.detail}
                          </p>
                        </div>
                      </div>
                      {address._id === currentAddress?._id && (
                        <IoCheckmarkDoneSharp size="32px" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
          </Stack>
        </Content>
      </Stack>
    </div>
  );
}
